import React from 'react';
import useGameStore from '../../store/gameStore';
import { SUPPORT_FAMILIES, getSupportRolesByFamily } from '../../data/supportRoles';
import { Building2, Crown, UserPlus } from 'lucide-react';

const eur = (v) => `${Math.round(v || 0).toLocaleString('fr-FR')} €`;

const RoleNode = ({ role, hired }) => (
  <div
    className={`flex items-center gap-2.5 rounded-lg border px-2.5 py-2 ${hired ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-ink-600 bg-ink-900/40 opacity-60'}`}
    data-testid={`orga-role-${role.id}`}
  >
    <img
      src={role.portrait}
      alt={role.name}
      loading="lazy"
      className={`w-10 h-10 rounded-full object-cover border ${hired ? 'border-emerald-400/60' : 'border-ink-600 grayscale opacity-50'}`}
    />
    <div className="min-w-0">
      {hired ? (
        <>
          <p className="text-xs font-bold text-slate-100 truncate">{role.name}</p>
          <p className="text-[10px] text-slate-400 truncate">{role.role}</p>
          <p className="text-[10px] font-mono text-emerald-300">{eur(role.monthlyCost)}/mois{/* i18n-keep */}</p>
        </>
      ) : (
        <>
          <p className="text-xs font-bold text-slate-500 truncate">{role.role}</p>
          <p className="text-[10px] text-slate-600 flex items-center gap-1"><UserPlus className="w-3 h-3" /> À recruter{/* i18n-keep */}</p>
        </>
      )}
    </div>
  </div>
);

/** 01/07 — Organigramme visuel du siège social (dynamique groupe). */
export const OrganigrammeGroupe = () => {
  const company = useGameStore(s => s.company);
  const hiredSupport = useGameStore(s => s.hiredSupport) || {};
  const activeInvestments = useGameStore(s => s.activeInvestments) || {};
  const siege = !!activeInvestments['inv-office-01'];
  const hiredCount = Object.keys(hiredSupport).length;
  const totalCout = SUPPORT_FAMILIES.flatMap(f => getSupportRolesByFamily(f.id))
    .filter(r => hiredSupport[r.id])
    .reduce((s, r) => s + r.monthlyCost, 0);

  return (
    <div className="space-y-0" data-testid="organigramme-groupe">
      {/* Direction générale */}
      <div className="flex justify-center">
        <div className="rounded-lg border border-purple-500/50 bg-purple-500/10 px-5 py-3 text-center" data-testid="orga-direction">
          <Crown className="w-5 h-5 text-purple-300 mx-auto mb-1" />
          <p className="text-sm font-black text-purple-200">{company?.name || 'Votre enseigne'}</p>
          <p className="text-[10px] uppercase tracking-wider text-slate-400">Direction générale{/* i18n-keep */}</p>
        </div>
      </div>
      <div className="flex justify-center"><div className="w-px h-5 bg-ink-500" /></div>

      {/* Siège social */}
      <div className="flex justify-center">
        <div className={`rounded-lg border px-5 py-2.5 text-center ${siege ? 'border-amber-500/50 bg-amber-500/10' : 'border-ink-600 bg-ink-900/40 opacity-70'}`} data-testid="orga-siege">
          <Building2 className={`w-5 h-5 mx-auto mb-1 ${siege ? 'text-amber-300' : 'text-slate-600'}`} />
          <p className={`text-xs font-bold ${siege ? 'text-amber-200' : 'text-slate-500'}`}>
            {siege ? 'Siège social' : 'Siège social — à inaugurer'}{/* i18n-keep */}
          </p>
          {siege && (
            <p className="text-[10px] font-mono text-slate-400">{hiredCount} fonction(s) · {eur(totalCout)}/mois{/* i18n-keep */}</p>
          )}
        </div>
      </div>
      <div className="flex justify-center"><div className="w-px h-5 bg-ink-500" /></div>
      <div className="mx-8 h-px bg-ink-500" />

      {/* Familles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-5">
        {SUPPORT_FAMILIES.map((fam) => {
          const roles = getSupportRolesByFamily(fam.id);
          if (roles.length === 0) return null;
          const famHired = roles.filter(r => hiredSupport[r.id]).length;
          return (
            <div key={fam.id} className="rounded-lg border border-ink-600 bg-ink-800/40 p-3" data-testid={`orga-famille-${fam.id}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-300">{fam.name}</p>
                <span className={`text-[10px] font-mono ${famHired > 0 ? 'text-emerald-300' : 'text-slate-600'}`}>{famHired}/{roles.length}</span>
              </div>
              <div className="space-y-1.5">
                {roles.map(role => <RoleNode key={role.id} role={role} hired={!!hiredSupport[role.id]} />)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrganigrammeGroupe;
